import { domainTitle, HATCH_FILL, levelOf, STRINGS, type Band, type ShownDomain } from './report';
import { REPORT } from './tokens';

const HEIGHT = 230;
const PLOT_LEFT = 20;
const CHART_GAP = 24;
const BASELINE = 200;
const PLOT_HEIGHT = 160;
const PAIR_GAP = 5;
const BAR_RADIUS = 4;
const AXIS_X = 12;
const CAPTION_Y = 28;
const LABEL_Y = 218;
const LABEL_LINE = 13;
const LABEL_UNIT_PX = 6.3;
const LABEL_SLOT_GAP = 4;
const NARROW = new Set([...'iljtfr.,\'’ ']);
const WIDE = new Set([...'mwMW']);

export function labelUnits(text: string): number {
  return [...text].reduce((units, ch) => {
    if (WIDE.has(ch)) return units + 1.5;
    if (NARROW.has(ch)) return units + 0.6;
    if (ch === ch.toUpperCase() && ch !== ch.toLowerCase()) return units + 1.4;
    return units + 1;
  }, 0);
}

export function labelLines(name: string, slotPx: number): string[] {
  if (labelUnits(name) * LABEL_UNIT_PX <= slotPx || !name.includes(' ')) return [name];
  const words = name.split(' ');
  let best = 1;
  let bestGap = Number.POSITIVE_INFINITY;
  for (let i = 1; i < words.length; i += 1) {
    const gap = Math.abs(words.slice(0, i).join(' ').length - words.slice(i).join(' ').length);
    if (gap < bestGap) {
      bestGap = gap;
      best = i;
    }
  }
  return [words.slice(0, best).join(' '), words.slice(best).join(' ')];
}

export const HATCH_OLD_ID = 'progress-report-hatch-old';
export const HATCH_MID_ID = 'progress-report-hatch-mid';

export function HatchPatterns() {
  return (
    <svg width={0} height={0} style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
      <defs>
        <pattern id={HATCH_OLD_ID} width={4} height={4} patternUnits="userSpaceOnUse">
          <rect width={4} height={4} fill={HATCH_FILL} />
          <path d="M-1,-1 L5,5 M-1,3 L1,5 M3,-1 L5,1" stroke="#FFFFFF" strokeWidth={0.75} />
        </pattern>
        <pattern id={HATCH_MID_ID} width={4} height={4} patternUnits="userSpaceOnUse">
          <rect width={4} height={4} fill={HATCH_FILL} />
          <path d="M-1,5 L5,-1 M-1,1 L1,-1 M3,5 L5,3" stroke="#FFFFFF" strokeWidth={0.75} />
        </pattern>
      </defs>
    </svg>
  );
}

export function hatchFor(index: number, shown: number): string {
  return `url(#${shown === 3 && index === 0 ? HATCH_OLD_ID : HATCH_MID_ID})`;
}

export function barWidth(shown: number): number {
  return shown === 3 ? 20 : 32;
}

function fit(text: string, slotPx: number) {
  return labelUnits(text) * LABEL_UNIT_PX > slotPx ? { textLength: slotPx, lengthAdjust: 'spacingAndGlyphs' as const } : {};
}

function IndicatorLabel({ name, x, slotPx }: { name: string; x: number; slotPx: number }) {
  const lines = labelLines(name, slotPx);
  if (lines.length === 1) {
    return (
      <text data-testid="skills-label" data-name={name} x={x} y={LABEL_Y} textAnchor="middle" fill={REPORT.ink} fontWeight={600} {...fit(name, slotPx)}>
        {name}
      </text>
    );
  }
  const top = LABEL_Y - LABEL_LINE / 2;
  return (
    <text data-testid="skills-label" data-name={name} x={x} y={top} textAnchor="middle" fill={REPORT.ink} fontWeight={600}>
      {lines.map((line, i) => (
        <tspan key={line} x={x} y={top + i * LABEL_LINE} {...fit(line, slotPx)}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

// Speaking and writing each get their own chart and scale, side by side.
// Every indicator gets the same slot width, so a chart is as wide as its
// indicator count needs.
export function SkillsChart({ domains, bands, top, shown }: { domains: ShownDomain[]; bands: Band[]; top: number; shown: number }) {
  const charted = domains.filter((domain) => domain.indicators.length > 0);
  const count = charted.reduce((sum, domain) => sum + domain.indicators.length, 0);
  const slot = (REPORT.sheet.inner - CHART_GAP * (charted.length - 1) - PLOT_LEFT * charted.length) / Math.max(count, 1);
  return (
    <div style={{ display: 'flex', gap: CHART_GAP }}>
      {charted.map((domain) => (
        <DomainChart key={domain.domain} domain={domain} bands={bands} top={top} shown={shown} slot={slot} />
      ))}
    </div>
  );
}

function DomainChart({ domain, bands, top, shown, slot }: { domain: ShownDomain; bands: Band[]; top: number; shown: number; slot: number }) {
  const width = PLOT_LEFT + slot * domain.indicators.length;
  const unit = PLOT_HEIGHT / Math.max(top, 1);
  const barW = barWidth(shown);
  const groupW = shown * barW + (shown - 1) * PAIR_GAP;
  const cx = (i: number) => PLOT_LEFT + slot * (i + 0.5);
  const slotPx = slot - LABEL_SLOT_GAP;

  return (
    <svg
      data-testid="skills-chart"
      data-domain={domain.domain}
      viewBox={`0 0 ${width} ${HEIGHT}`}
      width={width}
      height={HEIGHT}
      role="img"
      aria-label={`${domainTitle(domain.domain)} indicator levels, most recent ${shown} benchmarks`}
      style={{ display: 'block', flexShrink: 0, fontFamily: REPORT.font, fontSize: 12 }}
    >
      {Array.from({ length: top }, (_, i) => i + 1).map((level) => {
        const y = BASELINE - unit * level;
        return (
          <g key={`level-${level}`}>
            <rect x={PLOT_LEFT} y={y} width={width - PLOT_LEFT} height={1} fill={REPORT.grid} />
            <text data-testid="skills-axis" x={AXIS_X} y={y + 4} textAnchor="end" fill={REPORT.faint} fontWeight={700}>
              {level}
            </text>
          </g>
        );
      })}
      <rect x={PLOT_LEFT} y={BASELINE} width={width - PLOT_LEFT} height={2} fill={REPORT.ink} />
      <text
        data-testid={`skills-group-${domain.domain}`}
        x={PLOT_LEFT + (width - PLOT_LEFT) / 2}
        y={CAPTION_Y}
        textAnchor="middle"
        fill={REPORT.muted}
        fontWeight={800}
        letterSpacing={1}
      >
        {STRINGS.domainIndicators(domain.domain)}
      </text>
      {domain.indicators.map((indicator, i) => {
        const left = cx(i) - groupW / 2;
        return (
          <g key={`${domain.domain}-${indicator.name}`} data-testid="skills-indicator">
            {indicator.scores.map((score, j) => {
              const x = left + j * (barW + PAIR_GAP);
              const current = j === shown - 1;
              const level = levelOf(bands, score);
              if (level === 0) {
                return (
                  current &&
                  shown > 1 && (
                    <rect
                      key={j}
                      data-testid="skills-unscored"
                      x={x + 1}
                      y={BASELINE - unit + 1}
                      width={barW - 2}
                      height={unit - 2}
                      rx={BAR_RADIUS}
                      fill={REPORT.screenBg}
                      stroke={REPORT.faint}
                      strokeWidth={2}
                      strokeDasharray="4 3"
                    />
                  )
                );
              }
              const height = level * unit;
              return (
                <rect
                  key={j}
                  data-testid={current ? 'skills-current' : 'skills-previous'}
                  data-level={level}
                  x={x}
                  y={BASELINE - height}
                  width={barW}
                  height={height}
                  rx={BAR_RADIUS}
                  fill={current ? bands[level - 1]?.color || HATCH_FILL : hatchFor(j, shown)}
                />
              );
            })}
            <IndicatorLabel name={indicator.name} x={cx(i)} slotPx={slotPx} />
          </g>
        );
      })}
    </svg>
  );
}
